/**
 * Storage Keys - Single source of truth for localStorage keys
 *
 * RULES:
 * 1. ALWAYS use these constants to access localStorage
 * 2. NEVER use string literals for storage keys
 * 3. Prefix standard: 'asgard-' for consistency
 */

// =============================================================================
// APPLICATION STORAGE KEYS
// =============================================================================

export const STORAGE_KEYS = {
  // ----- Core Application -----
  /** Theme preference (light/dark/system) */
  THEME: 'asgard-studio-theme',
  /** General user settings */
  SETTINGS: 'asgard-studio-settings',
  /** Preferred language */
  LANGUAGE: 'asgard-language',
  /** Language storage version for migrations */
  LANGUAGE_VERSION: 'asgard-language-version',
  /** Auto-detected language */
  LANGUAGE_AUTO_DETECTED: 'asgard-language-auto-detected',
  /** Setup wizard completed status */
  SETUP_WIZARD_COMPLETED: 'asgard-wizard-completed',
  /** Cloud cost warning dismissed (per-component) */
  CLOUD_COST_WARNING_DISMISSED: 'asgard-cloud-cost-warning-dismissed',

  // ----- Subtitle Styles -----
  /** Current subtitle style.
   *  v2: SubtitleStyleConfig fields were renamed to snake_case (= backend wire
   *  format, RULE 1.5). The key bump discards old camelCase data — users fall
   *  back to DEFAULT_SUBTITLE_STYLE instead of silently sending dead fields. */
  SUBTITLE_STYLE: 'asgard-subtitle-style-v2',
  /** Emoticon subtitles config */
  EMOTICON_CONFIG: 'asgard-emoticon-config',
  /** Recent subtitle presets */
  SUBTITLE_PRESETS_RECENT: 'asgard-subtitle-presets-recent',
  /** Custom subtitle presets (v2: snake_case configs, see SUBTITLE_STYLE) */
  SUBTITLE_PRESETS_CUSTOM: 'asgard-subtitle-presets-custom-v2',
  /** Selected subtitle preset ID */
  SUBTITLE_SELECTED_PRESET: 'asgard-subtitle-selected-preset',

  // ----- Thumbnail Studio -----
  /** Thumbnail studio autosave */
  THUMBNAIL_AUTOSAVE: 'asgard-thumbnail-autosave',
  /** Pending canvas state from SEO generator deep-link (consumed once by CanvasTab on mount) */
  THUMBNAIL_PENDING_CANVAS: 'asgard-thumbnail-pending-canvas',
  /** Saved templates */
  TEMPLATES: 'asgard-templates',
  /** Favorite templates */
  TEMPLATE_FAVORITES: 'asgard-template-favorites',
  /** Brand kits */
  BRAND_KITS: 'asgard-brand-kits',

  // ----- AI Generation -----
  /** AI generation draft */
  AI_GENERATION_DRAFT: 'asgard-ai-generation-draft',
  /** AI generation history cache */
  AI_GENERATION_HISTORY: 'asgard-ai-generation-history',

  // ----- Image Generation -----
  /** Fooocus style favorites */
  FOOOCUS_FAVORITES: 'asgard-fooocus-favorites',
  /** Image generation history */
  IMAGE_HISTORY: 'asgard-image-history',
  /** Fooocus background generation history */
  FOOOCUS_GENERATION_HISTORY: 'asgard-fooocus-generation-history',

  // ----- Referral System -----
  /** Stored referral code from URL (used during signup) */
  REFERRAL_CODE: 'asgard-referral-code',

  // ----- Geo-Restriction -----
  /** Cached geo-detection result (tier, country, restrictions) */
  GEO_TIER: 'asgard-geo-tier',
  /** Detected country code */
  GEO_COUNTRY: 'asgard-geo-country',

  // ----- Video Assistant -----
  /** Whether the video assistant chat panel is open */
  VIDEO_ASSISTANT_CHAT_OPEN: 'asgard-video-assistant-open',

  // ----- Form Persistence -----
  /** Persisted video config form state */
  VIDEO_CONFIG_FORM_STATE: 'videoConfigFormState',

  // ----- Fooocus -----
  /** Cached Fooocus styles list with TTL */
  FOOOCUS_STYLES_CACHE: 'fooocus_styles_cache',

  // ----- Notifications -----
  /** User preference for audio notifications */
  AUDIO_NOTIFICATIONS_ENABLED: 'audioNotificationsEnabled',

  // ----- Testing -----
  /** E2E test mode flag — lives in sessionStorage (NOT localStorage).
   *  sessionStorage is isolated from localStorage.clear(), so the harness
   *  bypass survives app state resets. Bootstrap via '?e2e=true' URL param;
   *  isE2ETestMode() writes it to sessionStorage on first read. */
  E2E_TEST_MODE: 'e2e_test_mode',

  // ----- Language Preferences -----
  /** User's preferred audio generation language (ISO 639-1 code, e.g. 'pt') */
  PREFERRED_AUDIO_LANGUAGE: 'asgard.preferred_audio_language',
  /** User's preferred subtitle/translation language (ISO 639-1 code, e.g. 'pt') */
  PREFERRED_SUBTITLE_LANGUAGE: 'asgard.preferred_subtitle_language',

  // ----- Music Video Mode -----
  /** Pending music context for the video page (consumed once on mount) */
  MUSIC_VIDEO_CONTEXT: 'asgard-music-video-context',

  // ----- YouTube -----
  /** Last chosen output folder for YouTube downloads */
  YOUTUBE_DOWNLOAD_FOLDER: 'asgard-youtube-download-folder',
  /** Recent YouTube download entries (persisted list) */
  YOUTUBE_RECENT_DOWNLOADS: 'asgard-youtube-recent-downloads',
  /** User's last chosen SEO localization model in the publish form (persists their override of the recommended default) */
  YOUTUBE_SEO_MODEL: 'asgard-youtube-seo-model',

  // ----- Cross-Tab Generation State -----
  /** Map of in-flight generation jobs (jobId -> GenerationJob) */
  GENERATION_ACTIVE_JOBS: 'asgard-generation-active-jobs',
  /** Map of recent terminal job results (tabMode -> GenerationJob[]) */
  GENERATION_RECENT_RESULTS: 'asgard-generation-recent-results',
  /** Per-browser-tab owner id (sessionStorage) for poller election */
  GENERATION_OWNER_TAB_ID: 'asgard-generation-owner-tab-id',
  /** Build version of the app that wrote the current persisted generation state.
   *  On rehydrate, mismatched version → wipe active-jobs + recent-results.
   *  Prevents ghost "Generating..." cards from prior deploys. */
  GENERATION_PERSISTENCE_VERSION: 'asgard-generation-persistence-version',
} as const;

// =============================================================================
// AUTHENTICATION TOKENS
// =============================================================================

export const AUTH_STORAGE_KEYS = {
  /** Primary authentication token */
  AUTH_TOKEN: 'auth_token',
  /** Firebase ID token */
  FIREBASE_TOKEN: 'firebase_id_token',
} as const;

// =============================================================================
// LIMITS
// =============================================================================

export const STORAGE_LIMITS = {
  /** Maximum recent presets */
  MAX_RECENT_PRESETS: 5,
  /** Maximum history items */
  MAX_HISTORY_ITEMS: 50,
  /** Safety ceiling on simultaneously persisted active jobs (across all tabs) */
  MAX_ACTIVE_JOBS: 20,
  /** Max terminal job results retained per tab */
  MAX_RESULTS_PER_TAB: 5,
  /** Results older than this are dropped on rehydrate (24h) */
  RESULT_TTL_MS: 24 * 60 * 60 * 1000,
  /** If a job's updatedAt is older than this, the owning tab is presumed dead
   *  and another tab may adopt ownership (10s) */
  JOB_OWNER_STALE_MS: 10_000,
  /** Active jobs whose updatedAt is older than this are dropped on rehydrate.
   *  Prevents ghost "Generating..." UI when the app was closed mid-job or the
   *  job predates the current build.
   *  Set to 4 hours to cover long-running jobs (e.g. 73-language subtitle
   *  translation + Track A WhisperX pipeline can exceed 60 min). */
  ACTIVE_JOB_MAX_AGE_MS: 4 * 60 * 60 * 1000,
} as const;

// =============================================================================
// TYPE EXPORTS
// =============================================================================

export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];
export type AuthStorageKey = (typeof AUTH_STORAGE_KEYS)[keyof typeof AUTH_STORAGE_KEYS];
